import React, { useState, useEffect } from 'react';
import { useStripe } from '../../../contexts/StripeContext';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import { AlertCircle } from 'lucide-react';


const SubscriptionTab = ({ user, userProfile }) => {
  const { createCheckoutSession } = useStripe();
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Calculate trial status
  const getTrialStatus = () => {
    if (!userProfile?.trial_start_date) return { status: 'inactive', daysRemaining: 0 };

    const trialStart = new Date(userProfile?.trial_start_date);
    const now = new Date();
    const daysSinceStart = Math.floor((now - trialStart) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, 7 - daysSinceStart);

    return {
      status: daysRemaining > 0 ? 'active' : 'expired',
      daysRemaining
    };
  };

  const trialStatus = getTrialStatus();

  const handleUpgradeClick = (planType) => {
    const monthlyUrl = 'https://buy.stripe.com/28E9AN9Fodgv9q5eiodEs01';
    const annualUrl = 'https://buy.stripe.com/cNi00dbNwccreKpb6cdEs00';
    
    const url = planType === 'monthly' ? monthlyUrl : annualUrl;
    
    if (url) {
      window.location.href = url;
    } else {
      setMessage({ 
        type: 'error', 
        text: 'Payment URL not configured. Please contact support.' 
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Subscription & Billing
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Manage your subscription plan and payment methods
        </p>
      </div>

      {message?.text && (
        <div
          className={`p-4 rounded-lg ${
            message?.type === 'success' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
          }`}
        >
          {message?.text}
        </div>
      )}

      {/* Trial Information Notice */}
      {userProfile?.subscription_status === 'trial' && (
        <div className="p-4 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">7-Day Free Trial</h4>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                After 7 days, your trip settings and inventory will be automatically deleted. You can continue using GalleyPlan by creating a new trip for another 7-day cycle, or upgrade to Premium for unlimited access and trips up to 60 days.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Status */}
      <div className="p-6 rounded-lg border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Current Plan
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                {trialStatus?.status === 'active' ? 'Trial Plan' : trialStatus?.status === 'expired' ? 'Trial Expired' : 'Free Plan'}
              </p>
              <p className="text-sm text-muted-foreground">
                {trialStatus?.status === 'active'
                  ? `${trialStatus?.daysRemaining} days remaining`
                  : trialStatus?.status === 'expired' ? 'Upgrade to continue using premium features' : 'Start your 7-day free trial'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                $0.00
              </p>
              <p className="text-sm text-muted-foreground">per month</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button 
              onClick={() => handleUpgradeClick('monthly')} 
              loading={loading} 
              variant="outline"
              fullWidth
            >
              Monthly Subscription
            </Button>
            <Button 
              onClick={() => handleUpgradeClick('annual')} 
              loading={loading} 
              fullWidth
            >
              Annual Subscription
            </Button>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Payment Method
        </h3>
        <div className="p-6 rounded-lg border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">No payment method on file</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add a payment method to upgrade your subscription
              </p>
            </div>
            <Button variant="outline" onClick={() => setShowPaymentModal(true)}>
              Add Payment
            </Button>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Billing History
        </h3>
        <div className="p-6 rounded-lg border text-center" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}>
          <p className="text-sm text-muted-foreground">No billing history available</p>
          <p className="text-xs text-muted-foreground mt-1">
            Your billing history will appear here after your first payment
          </p>
        </div>
      </div>

      {/* Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Add Payment Method"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Payment methods are managed securely through Stripe during checkout.
          </p>
          <Button onClick={() => { setShowPaymentModal(false); handleUpgradeClick('monthly'); }} loading={loading} fullWidth>
            Proceed to Checkout
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionTab;